// Analytics tracking utilities for Style.re.
// Supports first-party capture, GA4, Microsoft Clarity, TikTok Pixel, X/Twitter Pixel, and Meta Pixel.

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

type DeviceType = 'desktop' | 'mobile' | 'tablet';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
    ttq?: any;
    twq?: (...args: any[]) => void;
    __stylereAnalyticsInitialized?: boolean;
  }
}

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const GTM_ID = import.meta.env.VITE_GTM_CONTAINER_ID;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;
const TIKTOK_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID;
const X_PIXEL_ID = import.meta.env.VITE_X_PIXEL_ID || import.meta.env.VITE_TWITTER_PIXEL_ID;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;
const STYLE_RE_META_PIXEL_ID = '913841745018490';
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.stylere.app';

type RemoteAnalyticsConfig = {
  ga4MeasurementId?: string;
  gtmContainerId?: string;
  clarityProjectId?: string;
  tiktokPixelId?: string;
  xPixelId?: string;
  metaPixelId?: string;
};

let remoteConfig: RemoteAnalyticsConfig = {};

function appendScript(src: string, id: string, async = true): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;
  if (document.getElementById(id)) return null;
  const script = document.createElement('script');
  script.id = id;
  script.async = async;
  script.src = src;
  document.head.appendChild(script);
  return script;
}

function upsertInlineScript(id: string, code: string): void {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.text = code;
  document.head.appendChild(script);
}

function getOrCreateId(key: string): string {
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = `${Date.now().toString(36)}-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
    return id;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

function getDeviceType(): DeviceType {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/ipad|tablet|kindle|silk/i.test(ua)) return 'tablet';
  if (/mobi|iphone|android/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getUtmParams(): AnalyticsParams {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    term: params.get('utm_term') || undefined,
    content: params.get('utm_content') || undefined,
  };
}

export function getFunnelStep(path: string): string {
  if (path === '/') return 'home';
  if (path.includes('/get-delivery') || path.includes('/landing') || path.includes('/skip-the-mall')) return 'landing_page';
  if (path.includes('/store-selection') || path.includes('/schedule-pickup')) return 'delivery_flow';
  if (path.includes('/store-details')) return 'store_details';
  if (path.includes('/order-details')) return 'order_details';
  if (path.includes('/delivery-details')) return 'delivery_details';
  if (path.includes('/payment') || path.includes('/checkout')) return 'checkout';
  if (path.includes('/complete') || path.includes('/confirmation')) return 'conversion';
  if (path.includes('/tracking') || path.includes('/track/')) return 'tracking';
  if (path.includes('/auth') || path.includes('/login') || path.includes('/signup') || path.includes('/register')) return 'auth';
  if (path.includes('/wallet') || path.includes('/settings') || path.includes('/pickup-history')) return 'account';
  return 'content';
}

export function initAnalytics(): void {
  if (typeof window === 'undefined' || window.__stylereAnalyticsInitialized) return;
  window.__stylereAnalyticsInitialized = true;

  // Runtime API config is preferred. Public pixel IDs also need a narrow
  // fallback so tracking still loads while the API DNS lane is degraded.
  const gtmId = remoteConfig.gtmContainerId || GTM_ID;
  const ga4Id = remoteConfig.ga4MeasurementId || GA4_ID;
  const clarityId = remoteConfig.clarityProjectId || CLARITY_ID;
  const tiktokId = remoteConfig.tiktokPixelId || TIKTOK_ID;
  const xPixelId = remoteConfig.xPixelId || X_PIXEL_ID;
  const metaPixelId = remoteConfig.metaPixelId || META_PIXEL_ID || STYLE_RE_META_PIXEL_ID;

  if (gtmId) {
    window.dataLayer = window.dataLayer || [];
    upsertInlineScript('stylere-gtm-init', `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`);
  }

  if (ga4Id) {
    appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`, 'stylere-ga4');
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || ((...args: unknown[]) => { window.dataLayer?.push(args); });
    window.gtag('js', new Date());
    window.gtag('config', ga4Id, { send_page_view: false });
  }

  if (clarityId) {
    upsertInlineScript('stylere-clarity-init', `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, 'clarity', 'script', '${clarityId}');`);
  }

  if (tiktokId) {
    upsertInlineScript('stylere-tiktok-init', `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i='https://analytics.tiktok.com/i18n/pixel/events.js';ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement('script');o.type='text/javascript',o.async=!0,o.src=i+'?sdkid='+e+'&lib='+t;var a=document.getElementsByTagName('script')[0];a.parentNode.insertBefore(o,a)};ttq.load('${tiktokId}');}(window, document, 'ttq');`);
  }

  if (xPixelId) {
    upsertInlineScript('stylere-x-pixel-init', `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');twq('config','${xPixelId}');`);
  }

  if (metaPixelId && !window.fbq) {
    upsertInlineScript('stylere-meta-pixel-init', `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaPixelId}');`);
  }
}

export async function initAnalyticsWithRemoteConfig(): Promise<void> {
  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 1500);
    const response = await fetch(`${API_BASE}/api/analytics/config`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    window.clearTimeout(timeout);
    if (response.ok) remoteConfig = await response.json();
  } catch {
    // Local/third-party analytics still initialize from build-time env when config fetch fails.
  }
  initAnalytics();
}

function sendFirstParty(eventName: string, params: AnalyticsParams = {}): void {
  if (typeof window === 'undefined') return;

  const payload = {
    event: eventName,
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer,
    sessionId: getOrCreateId('stylere_session_id'),
    visitorId: getOrCreateId('stylere_visitor_id'),
    device: getDeviceType(),
    funnelStep: getFunnelStep(window.location.pathname),
    timestamp: new Date().toISOString(),
    ...getUtmParams(),
    ...params,
  };

  const body = JSON.stringify(payload);
  const url = `${API_BASE}/api/analytics/collect`;

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

function trackThirdParty(eventName: string, params?: AnalyticsParams): void {
  if (window.gtag) window.gtag('event', eventName, params || {});
  if (window.clarity) window.clarity('event', eventName);
  if (window.ttq?.track) window.ttq.track(eventName, params || {});
  if (window.twq) window.twq('event', eventName, params || {});
  if (window.fbq) window.fbq('trackCustom', eventName, params || {});
}

export const trackEvent = (eventName: string, params?: AnalyticsParams) => {
  sendFirstParty(eventName, params);
  trackThirdParty(eventName, params);
};

export const analytics = {
  pageView: (path: string) => {
    const params = { page_path: path, funnel_step: getFunnelStep(path), ...getUtmParams() };
    sendFirstParty('page_view', params);
    if (window.gtag) window.gtag('event', 'page_view', params);
    if (window.fbq) window.fbq('track', 'PageView');
    if (window.ttq?.page) window.ttq.page();
    if (window.twq) window.twq('event', 'PageView', params);
    if (window.clarity) window.clarity('event', `page_view:${getFunnelStep(path)}`);
  },

  signUp: (method: string = 'email') => {
    trackEvent('sign_up', { method, funnel_step: 'auth' });
    if (window.fbq) window.fbq('track', 'CompleteRegistration', { method });
    if (window.ttq?.track) window.ttq.track('CompleteRegistration', { method });
  },

  beginCheckout: (value: number, storeId?: string) => {
    const params = { value, currency: 'USD', store_id: storeId, funnel_step: 'checkout' };
    trackEvent('begin_checkout', params);
    if (window.fbq) window.fbq('track', 'InitiateCheckout', params);
    if (window.ttq?.track) window.ttq.track('InitiateCheckout', params);
  },

  purchase: (value: number, orderId: string) => {
    const params = { value, currency: 'USD', transaction_id: orderId, funnel_step: 'conversion' };
    trackEvent('purchase', params);
    if (window.fbq) window.fbq('track', 'Purchase', params);
    if (window.ttq?.track) window.ttq.track('CompletePayment', params);
  },

  selectStore: (storeName: string, storeId?: string) => {
    trackEvent('select_item', { item_name: storeName, item_id: storeId, funnel_step: 'delivery_flow' });
  },

  lead: (source: string) => {
    trackEvent('generate_lead', { source, funnel_step: 'landing_page' });
    if (window.fbq) window.fbq('track', 'Lead', { source });
    if (window.ttq?.track) window.ttq.track('SubmitForm', { source });
  },

  share: (method: string, referralCode: string) => {
    trackEvent('share', { method, content_type: 'referral', item_id: referralCode });
  },

  promoApplied: (code: string) => {
    trackEvent('select_promotion', { promotion_name: code });
  },
};
